import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { CommunityDTO } from '../DTO/CommunityDTO';
import { NewDTO } from '../DTO/NewDTO';
import { API_URL } from '../../environments/environment';
import { EsdevenimentDTO } from "../DTO/EsdevenimentDTO";
import { firstValueFrom, Observable, of } from "rxjs";
import { ShareInfoDTO } from '../DTO/ShareInfoDTO';
import { ResetPassRequestDTO } from '../DTO/ResetPassRequestDTO';
import { CommunityModuleDTO } from '../DTO/CommunityModuleDTO';
import { EventFilterDTO } from '../DTO/filters/EventFilterDTO';
import {InscriptionCategoryDTO, InscriptionsDTO} from "../DTO/InscriptionsDTO";
import {CustomFieldDTO} from "../DTO/CustomFieldDTO";
import {CustomFieldValueDTO} from "../DTO/CustomFieldValueDTO";
import {BonificationDTO} from "../DTO/BonificationDTO";
import {RecurrenceDTO} from "../DTO/RecurrenceDTO";
import {InscriptionSentDTO} from "../DTO/InscriptionSentDTO";
import { InscriptionFilterDTO } from '../DTO/filters/InscriptionFilterDTO';
import {TranslateService} from "@ngx-translate/core";
import {PortalDataDTO} from "../DTO/PAM/PortalDataDTO";
import {MandateDTO} from "../DTO/PAM/MandateDTO";
import {AxisDTO} from "../DTO/PAM/AxisDTO";
import {AreaDTO} from "../DTO/PAM/AreaDTO";
import {ODSDTO} from "../DTO/PAM/ODSDTO";
import {TypeDTO} from "../DTO/PAM/typeDTO";
import {ActionDTO} from "../DTO/PAM/ActionDTO";
import { InscriptionTotalPricesDTO } from '../DTO/prices/InscriptionTotalPricesDTO';
import {CenterDTO} from "../DTO/PriorAppointment/CenterDTO";
import {ServiceDTO} from "../DTO/PriorAppointment/ServiceDTO";
import {ReservationDTO} from "../DTO/PriorAppointment/ReservationDTO";
import {
  ActiveServiceIdsForDateDTO,
  ActiveServiceIdsForDateResponseDTO,
  AvailableTimesForDateRequestDTO,
  CheckEnabledDatesDTO, DisabledDaysAndMaxDateDTO,
  FindReservationDTO,
  HoursWithDataDTO,
  PeriodScheduleDTO, ResendCode,
  TimeAvailabilityDTO
} from "../DTO/PriorAppointment/PeriodsAndHoursDto";
import { ResourceSlotDTO } from '../DTO/PriorAppointment/ResourceSlotDTO';
import { TicketDTO } from '../DTO/TicketDTO';
import { CategoryDTO } from '../DTO/CategoryDTO';
import { TicketFilterDTO } from '../DTO/filters/TicketFilterDTO';
import { TicketMinDTO } from '../DTO/min/TicketMinDTO';
import { InscriptionMinDTO } from '../DTO/min/InscriptionMinDTO';
import { DateTime } from 'luxon';
import {WaitingListInfoDTO} from "../DTO/WaitingListInfoDTO";
import {WaitingListCodeMinDTO} from "../DTO/WaitingListCodeMinDTO";
import { PurchaseResultDTO } from '../DTO/tickets/PurchaseResultDTO';
import { TicketWaitingListInfoDTO } from '../DTO/tickets/TicketWaitingListInfoDTO';
import { TicketWaitingListCodeMinDTO } from '../DTO/tickets/TicketWaitingListCodeMinDTO';


export var COMMUNITY_CODES_BLACKLIST = [
  'robots',
  'assets',
  'img',
  'apple-app-site-association',
  'apple-touch-icon',
  'apple-touch-icon-precomposed',
  'apple-touch-icon-120x120',
  'apple-touch-icon-120x120-precomposed',
  'wp-content',
  'wp-admin',
  'wp-login',
  'wp-login.php',
  'backup',
  'wordpress',
  'download',
  'undefined',
  'plugins',
  'js',
  'sitemap'
];

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  navchange: EventEmitter<string> = new EventEmitter();

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService
    ) { }

  get<T>(url: string): Observable<T> {
    return this.httpClient.get<T>(url);
  }

  post<T>(url: string, body: any): Observable<T> {
    return this.httpClient.post<T>(url, body);
  }

  getShareInfo(url: string){
    return this.httpClient.get<ShareInfoDTO>(`${API_URL}/SocialMedia/getShareInfo?uri=${url}`);
  }

  getNews(community: string){
    return this.httpClient.get<NewDTO[]>(`${API_URL}/v2/communities/${community}/news?$orderBy=publishDate%20desc`);
  }

  countNews(community: string){
    return this.httpClient.get<number>(`${API_URL}/v2/communities/${community}/news/count`);
  }

  getNewsPaginated(top: number, skip: number, community: string){
    return this.httpClient.get<NewDTO[]>(`${API_URL}/v2/communities/${community}/news?$orderBy=publishDate%20desc&$top=${top}&$skip=${skip}`);
  }

  getNewById(id: string, community: string){
    return this.httpClient.get<NewDTO>(`${API_URL}/v2/communities/${community}/news/${id}`);
  }

  getCommunityByCode(code: string): Observable<CommunityDTO>{
    if (COMMUNITY_CODES_BLACKLIST.includes(code)){
      return of(null);
    }
    return this.httpClient.get<CommunityDTO>(`${API_URL}/v2/communities/${code}`);
  }


  itunesLookup(appId: string): Observable<any>{
    return this.httpClient.get<CommunityDTO>(`https://itunes.apple.com/es/lookup?id=${appId}&media=software&limit=1`);
  }

  appleAppData(community: string): Observable<any>{
    return this.httpClient.get<CommunityDTO>(`${API_URL}/v2/Communities/${community}/apple-app-data`);
  }

  public getEsdeveniments(community: number, start: Date, end: Date, top: number, skip: number): Observable<EsdevenimentDTO[]> {
    return this.httpClient.get<EsdevenimentDTO[]>(
      `${API_URL}/v2/Communities/${community}/Events?start=${start.toISOString()}&end=${end.toISOString()}&active=true&$orderBy=DateFrom asc&$top=${top}&$skip=${skip}`
    );
  }

  public countEsdeveniments(community: number , start: string, end: string): Observable<number> {
    return this.httpClient.get<number>(
      `${API_URL}/v2/Communities/${community}/Events/count?start=${start}&end=${end}&active=true&$orderBy=DateFrom desc&$top=50`
    );
  }

  public getEsdevenimentBySlug (community: string , slug: string): Observable<EsdevenimentDTO> {
    return this.httpClient.get<EsdevenimentDTO>(
      `${API_URL}/v2/Communities/${community}/Events/${slug}`
    );
  }

  public getEsdevenimentById(community: string, id: number): Observable<EsdevenimentDTO> {
    return this.httpClient.get<EsdevenimentDTO>(
      `${API_URL}/v2/Communities/${community}/Events/${id}`
    );
  }

  emitNavChangeEvent(code: string) {
    this.navchange.emit(code);
  }
  getNavChangeEmitter() {
    return this.navchange;
  }

  public GetGeoTown(communityCode: string): Observable<any> {
    return this.httpClient.get(`${API_URL}/v2.1/c/${communityCode}/geojson/polygon`);
  }

  public checkResetPassword(userid: number, token: string) {
    return this.httpClient.get(`${API_URL}/users/checkTokenValid/${userid}/${token}`);
  }

  public resetPassword(model: ResetPassRequestDTO): Observable<any> {
    return this.httpClient.post<any>(`${API_URL}/users/resetPassword`, model);
  }

  public GetModules(id: number): Observable<CommunityModuleDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${id}/modules`);
  }

  /** Includes child/submodules (API `showChildrenModules`). Use when resolving a module that may be nested. */
  public GetAllModules(id: number): Observable<CommunityModuleDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${id}/all-modules`);
  }

  public GetModuleById(communityId: number, moduleId: number): Observable<CommunityModuleDTO> {
    return this.get(`${API_URL}/v2/Communities/${communityId}/modules/${moduleId}`);
  }

  public getEventsCategories(id: number): Observable<any> {
    return this.get(`${API_URL}/Events/${id}/events-categories`);
  }

  public getEventsPaginated(top: number, skip: number, community: string) {
    return this.httpClient.get<NewDTO[]>(`${API_URL}/v2/communities/${community}/news?$orderBy=publishDate%20desc&$top=${top}&$skip=${skip}`);
  }

  public getEventsFiltered(communityCode: string, filters: EventFilterDTO): Observable<EsdevenimentDTO[]> {
    return this.httpClient.post<EsdevenimentDTO[]>(`${API_URL}/v2/Communities/${communityCode}/Events/events-filtered`, filters)
  }

  public countEventsFiltered(communityCode: string, filters: EventFilterDTO): Observable<number> {
    return this.httpClient.post<number>(`${API_URL}/v2/Communities/${communityCode}/Events/events-filtered/count`, filters)
  }

  //#region Tickets

  public GetTicketById(communityCode: string, ticketId: number) {
    return this.get(`${API_URL}/v2/Communities${communityCode}/Modules/Tickets/${ticketId}`);
  }

  public getFilteredTickets(communityCode: string, filters: any, top: number, skip: number): Observable<TicketMinDTO[]> {
    filters = {
      ...filters,
      top,
      skip
    };
    return this.httpClient.post<TicketMinDTO[]>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/filtered`, filters)
  }

  public countTickets(communityCode: string, filters: TicketFilterDTO): Observable<number> {
    return this.httpClient.post<number>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/filtered/count`, filters)
  }

  public postTicketWaitingList(communityCode: string, dto: TicketWaitingListInfoDTO): Observable<any> {
    return this.httpClient.post(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/waiting-list`, dto);
  }

  public checkTicketAccessCode(communityCode: string, dto: TicketWaitingListCodeMinDTO): Observable<TicketWaitingListInfoDTO> {
    return this.httpClient.post<TicketWaitingListInfoDTO>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/accessCodeCheck`, dto);
  }

  public updateTicketWaitingListAfterSuccess(communityCode: string, dto: TicketWaitingListInfoDTO): Observable<number> {
    return this.httpClient.post<number>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/updateWaitingListAfterSuccess`, dto);
  }

  public rejectFromTicketWaitingList(communityCode: string, id: number): Observable<void> {
    return this.httpClient.get<void>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets/rejectFromWaitingList/${id}`);
  }

  //#endregion

  //inscriptions
  public postInfoWaitingList(communityCode: string, waitingListInfo: WaitingListInfoDTO) {
    return this.httpClient.post(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/WaitingList`, waitingListInfo);
  }
  public getOneInscription(communityCode: string, id: number): Observable<InscriptionsDTO> {
    return this.get(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/getOneInscription/${id}`);
  }
  public getCustomFields(communityCode: string , id:number ): Observable<CustomFieldDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/getCustomFields/${id}`);
  }
  public getCustomFieldsValues(communityCode: string, id: number): Observable<CustomFieldValueDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/getCustomFieldsValues/${id}`);
  }
  public getBonificationsInscription(communityCode: string, id: number): Observable<BonificationDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/getBonificationsInscriptions/${id}`);
  }
  public getRecurrenceInscription(communityCode: string, id: number): Observable<RecurrenceDTO[]> {
    return this.get(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/getRecurrencesInscriptions/${id}`);
  }
  public sendInscription(
    communityCode: string,
    inscriptionRegs: InscriptionSentDTO[],
    token: string,
    rgpdAccepted: DateTime,
    accesCode?: string): Observable<any> {
      const date = rgpdAccepted.toISO({ suppressMilliseconds: true, includeOffset: false });
    return this.httpClient.post<void>(`${API_URL}/v2.2/Communities/${communityCode}/Modules/Inscriptions/send-inscription-form?token=${token}&rgpdAccepted=${date}&accesCode=${accesCode}`, inscriptionRegs);
  }
  public sendMailTowaitingList(communityCode: string, id: number , mail: string): Observable<void> {
    return this.httpClient.get<void>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/waitingList/${id}/${mail}` );
  }
  public getInscriptionCategories(communityCode: number): Observable<InscriptionCategoryDTO[]> {
    return this.httpClient.get<InscriptionCategoryDTO[]>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/categories-anonymous`);
  }
  public getInscriptionsFiltered(communityCode: string, filters: InscriptionFilterDTO): Observable<InscriptionMinDTO[]>{
    return this.httpClient.post<InscriptionMinDTO[]>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/inscriptions-filtered`,
      filters
    )
  }
  public updateWaitingListAfterSuccess(communityCode: string, waitingList: WaitingListInfoDTO): Observable<number>{
    return this.httpClient.post<number>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/updateWaitingListAfterSucces`,
        waitingList
    )
  }
  public rejectWaitingLsit(communityCode: string, id:number): Observable<void>{
    return this.httpClient.get<void>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/rejectFromWaitingList/${id}`
    )
  }
  public countInscriptionsFiltered(communityCode: string, filters: InscriptionFilterDTO): Observable<number>{
      return this.httpClient.post<number>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/inscriptions-filtered/count`,
      filters
    )
  }
  public calcFinalPrice(communityCode: string, inscriptionId: number, regs: InscriptionSentDTO[]) {
    return firstValueFrom(this.httpClient.post<InscriptionTotalPricesDTO>(`${API_URL}/v2.2/Communities/${communityCode}/Modules/Inscriptions/${inscriptionId}/calc-final-price`,
      regs
    ))
  }
  public postWaitingList(communityCode: string, id: number, dto: any): Observable<any> {
    return this.httpClient.post<any>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/${id}/waiting-list`, dto);
  }

   public Inscriptions_ReservePlaces(communityCode: string, inscriptionId: number, request: any , accesCode? :string): Observable<any> {
    return this.httpClient.post(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/${inscriptionId}/reserved-places?accesCode=${accesCode}`, request);
  }

  public Inscriptions_CancelPlaces(communityCode: string, inscriptionId: number, token: string): Observable<any> {
    return this.httpClient.delete(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/${inscriptionId}/reserved-places?reserveToken=${token}`);
  }
  public checkAccesCode(communityCode: string, objectAccesCode: WaitingListCodeMinDTO): Observable<WaitingListInfoDTO>{
    return this.httpClient.post<WaitingListInfoDTO>(`${API_URL}/v2/Communities/${communityCode}/Modules/Inscriptions/accessCodeCheck`,
        objectAccesCode
    )
  }

  //PAM
  public getDataPortal(communityCode: string , mandateId:number): Observable<PortalDataDTO> {
    return this.httpClient.get<PortalDataDTO>(`${API_URL}/v2/Communities/${communityCode}/PAM/PortalData/${mandateId}`);
  }
  public getMandates(communityCode: string): Observable<MandateDTO[]> {
    return this.httpClient.get<MandateDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/Mandates`);
  }
  public getAxis(communityCode: string , mandateId:number): Observable<AxisDTO[]> {
    return this.httpClient.get<AxisDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/Axis/${mandateId}`);
  }
  public getAreas(communityCode: string ): Observable<AreaDTO[]> {
    return this.httpClient.get<AreaDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/Areas/`);
  }
  public getODS(communityCode: string ): Observable<ODSDTO[]> {
    return this.httpClient.get<ODSDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/ODS/`);
  }
  public getTypes(communityCode: string ): Observable<TypeDTO[]> {
    return this.httpClient.get<TypeDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/Types/`);
  }
  getActionDetail(actionId:number , communityCode: string): Observable<any> {
    return this.httpClient.get<AxisDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/ActionDetail/${actionId}`);
  }
  getAxisDetail(axisId:number , communityCode: string): Observable<any> {
    return this.httpClient.get<AxisDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/AxisDetail/${axisId}`);
  }
  public getAreaDetail(communityCode: string, id:number ): Observable<AreaDTO> {
    return this.httpClient.get<AreaDTO>(`${API_URL}/v2/Communities/${communityCode}/PAM/AreaDetail/${id}`);
  }
  public getActionsByArea(communityCode: string, id:number ): Observable<ActionDTO[]> {
      return this.httpClient.get<ActionDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/ActionsByArea/${id}`);
  }
  public getTypeDetail(communityCode: string, id:number ): Observable<TypeDTO> {
    return this.httpClient.get<TypeDTO>(`${API_URL}/v2/Communities/${communityCode}/PAM/TypeDetail/${id}`);
  }
  public getActionsByType(communityCode: string, id:number ): Observable<ActionDTO[]> {
    return this.httpClient.get<ActionDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/ActionsByType/${id}`);
  }
  public getOdsDetail(communityCode: string, id:number ): Observable<ODSDTO> {
    return this.httpClient.get<ODSDTO>(`${API_URL}/v2/Communities/${communityCode}/PAM/ODSDetail/${id}`);
  }
  public getActionsByOds(communityCode: string, id:number ): Observable<ActionDTO[]> {
    return this.httpClient.get<ActionDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PAM/ActionsByOds/${id}`);
  }

  /** Query opcional para endpoints PriorAppointment que filtran por instancia de módulo (portal anónimo). */
  private priorAppointmentParams(accesCode?: string | null, communityModuleId?: number | null): HttpParams | undefined {
    let p = new HttpParams();
    let has = false;
    if (accesCode != null && accesCode !== '') {
      p = p.set('accesCode', accesCode);
      has = true;
    }
    if (communityModuleId != null && Number.isFinite(communityModuleId)) {
      p = p.set('communityModuleId', String(communityModuleId));
      has = true;
    }
    return has ? p : undefined;
  }

  //priorAppointment
  public getCenters(
    communityCode: string,
    accesCode?: string | null,
    communityModuleId?: number | null,
  ): Observable<CenterDTO[]> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Centers`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<CenterDTO[]>(url, params ? { params } : {});
  }
  public getServicesByCenterId(
    communityCode: string,
    centerId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<ServiceDTO[]> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Centers/${centerId}/Services`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<ServiceDTO[]>(url, params ? { params } : {});
  }
  public sendReservation(
    communityCode: string,
    reservation: ReservationDTO,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<any> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.post<any>(url, reservation, {
      responseType: 'text' as 'json',
      ...(params ? { params } : {}),
    });
  }

  public sendReservationHold(
    communityCode: string,
    reservation: ReservationDTO,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<ReservationDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/Hold`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.post<ReservationDTO>(url, reservation, params ? { params } : {});
  }

  public sendReservationWithPayment(
    communityCode: string,
    reservation: ReservationDTO,
    byPass = false,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<PurchaseResultDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/WithPayment`;
    let params = this.priorAppointmentParams(accesCode, communityModuleId) ?? new HttpParams();
    params = params.set('bypass', String(byPass));
    return this.httpClient.post<PurchaseResultDTO>(url, reservation, { params });
  }

  public markReservationPaymentError(
    communityCode: string,
    purchaseUuid: string,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<void> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/PaymentError`;
    let params = this.priorAppointmentParams(accesCode, communityModuleId) ?? new HttpParams();
    params = params.set('purchaseUuid', purchaseUuid);
    return this.httpClient.post<void>(url, {}, { params });
  }

  public markReservationHoldAsError(
    communityCode: string,
    reservationId: number,
    reservedToken: string,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<void> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/HoldError`;
    let params = this.priorAppointmentParams(accesCode, communityModuleId) ?? new HttpParams();
    params = params.set('reservationId', String(reservationId));
    params = params.set('reservedToken', reservedToken);
    return this.httpClient.post<void>(url, {}, { params });
  }
  public getDatesReserved(
    communityCode: string,
    serviceId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<string[]> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Services/${serviceId}/StartTimes`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<string[]>(url, params ? { params } : {});
  }
  public deleteReservation(communityCode: string, reservation: ReservationDTO ): Observable<void> {
    return this.httpClient.post<void>(`${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/Delete/`, reservation);
  }
  public getReservationDetail(communityCode: string, reservationSearch: FindReservationDTO): Observable<ReservationDTO> {
    return this.httpClient.post<ReservationDTO>(`${API_URL}/v2/Communities/${communityCode}/PriorAppointment/FindReservations/` , reservationSearch);
  }
  public resendCode(communityCode: string, resendCode: ResendCode): Observable<void> {
    return this.httpClient.post<void>(`${API_URL}/v2/Communities/${communityCode}/PriorAppointment/ResendCode` , resendCode);
  }
  public getScheduleObject(
    communityCode: string,
    serviceId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<PeriodScheduleDTO []> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Services/${serviceId}/GroupDaysOfWeekByPeriodArray`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<PeriodScheduleDTO []>(url, params ? { params } : {});
  }
  public generateAvailableTimes(communityCode: string, hoursWithData: HoursWithDataDTO): Observable<TimeAvailabilityDTO[]> {
    return this.httpClient.post<TimeAvailabilityDTO[]>(`${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/AvalibleTimes`, hoursWithData)
  }
  public disabledDates(
    communityCode: string,
    checkEnabledDates: CheckEnabledDatesDTO,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<DisabledDaysAndMaxDateDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/DisabledDates`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.post<DisabledDaysAndMaxDateDTO>(url, checkEnabledDates, params ? { params } : {});
  }

  public getCenterDetail(
    communityCode: string,
    centerId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<CenterDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/CenterDetail/${centerId}`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<CenterDTO>(url, params ? { params } : {});
  }

  public getServiceDetail(
    communityCode: string,
    serviceId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<ServiceDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Services/${serviceId}`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<ServiceDTO>(url, params ? { params } : {});
  }

  public getResourceSlots(
    communityCode: string,
    serviceId: number,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<ResourceSlotDTO[]> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Services/${serviceId}/Slots`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.get<ResourceSlotDTO[]>(url, params ? { params } : {});
  }

  public getActiveServiceIdsForDate(
    communityCode: string,
    body: ActiveServiceIdsForDateDTO,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<ActiveServiceIdsForDateResponseDTO> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/ActiveServiceIdsForDate`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.post<ActiveServiceIdsForDateResponseDTO>(url, body, params ? { params } : {});
  }

  public getAvailableTimesForDate(
    communityCode: string,
    body: AvailableTimesForDateRequestDTO,
    communityModuleId?: number | null,
    accesCode?: string | null,
  ): Observable<TimeAvailabilityDTO[]> {
    const url = `${API_URL}/v2/Communities/${communityCode}/PriorAppointment/Reservations/AvailableTimesForDate`;
    const params = this.priorAppointmentParams(accesCode, communityModuleId);
    return this.httpClient.post<TimeAvailabilityDTO[]>(url, body, params ? { params } : {});
  }

  //Tickets

  public getTickets(communityCode: string): Observable<TicketDTO[]> {
    return this.httpClient.get<TicketDTO[]>(`${API_URL}/v2/Communities/${communityCode}/Modules/Tickets`);
  }

  //

  public getCategoriesByModule(communityId: number, moduleType: string) {
    return this.httpClient.get<CategoryDTO[]>(`${API_URL}/CommunityModulesCategories/${communityId}/module?moduleType=${moduleType}`);
  }

}
